import type { MultipleChoiceArtifact } from '@dreamai/types';
import { ukDate } from './cards';
import {
  adaptFlightChoice,
  adaptHotelChoice,
  adaptRailChoice,
  type TravelOption,
} from './data';
import { adaptTravelChoices } from './travelOptionsModel';

export type MultipleChoiceArtifactWithSubtitle = MultipleChoiceArtifact & {
  subtitle?: string | null;
};

function dateRange(start?: string, end?: string | null): string | null {
  if (!start) return null;
  if (!end || start.slice(0, 10) === end.slice(0, 10)) return ukDate(start);

  const first = ukDate(start);
  const last = ukDate(end);
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  return `${sameMonth ? first.replace(/ \S+$/, '') : first} – ${last}`;
}

function adultsAndChildren(adults: number, children: number): string | null {
  const party = [
    adults > 0 ? `${adults} ${adults === 1 ? 'adult' : 'adults'}` : null,
    children > 0
      ? `${children} ${children === 1 ? 'child' : 'children'}`
      : null,
  ].filter((part): part is string => part !== null);
  return party.length ? party.join(', ') : null;
}

function subtitleForOption(option: TravelOption): string | null {
  if (option.kind === 'hotel') {
    return (
      [
        dateRange(option.checkIn, option.checkOut),
        adultsAndChildren(option.adults, option.children),
      ]
        .filter((part): part is string => part !== null)
        .join(', ') || null
    );
  }

  const dates = dateRange(option.legs[0]?.dep, option.returnDate);
  const travellers =
    option.kind === 'flight'
      ? `${option.passengers} traveller${option.passengers === 1 ? '' : 's'}`
      : null;
  return (
    [dates, travellers]
      .filter((part): part is string => part !== null)
      .join(', ') || null
  );
}

function adaptOption(card: ReturnType<typeof adaptTravelChoices>[number]) {
  if (card.kind === 'flight') {
    return adaptFlightChoice(card.artifactId, card.choice as never);
  }
  if (card.kind === 'hotel') {
    return adaptHotelChoice(card.artifactId, card.choice as never);
  }
  return adaptRailChoice(card.artifactId, card.choice as never);
}

export function travelSubtitle(
  artifact: MultipleChoiceArtifactWithSubtitle,
): string | null {
  const provided = artifact.subtitle?.trim();
  if (provided) return provided;

  const option = adaptTravelChoices({
    id: artifact.id,
    title: artifact.title,
    choices: artifact.choices,
  })
    .map(adaptOption)
    .find((candidate): candidate is TravelOption => candidate !== null);
  return option ? subtitleForOption(option) : null;
}
